import React, { useState } from 'react';
import TrialGate from './TrialGate';
import {
  generateAccessControlPolicy,
  generateIncidentResponsePolicy,
  generateConfigManagementPolicy,
  generateMediaProtectionPolicy,
  generateSystemCommPolicy,
  generateTrainingPolicy,
  generateRiskAssessmentPolicy,
} from './generateTemplates';

const TEMPLATES = [
  {
    domain:    'AC',
    name:      'Access Control',
    templates: [
      {
        id:          'ac-policy',
        title:       'Access Control Policy',
        description: 'Covers user account management, least privilege, remote access, and session controls. Maps to AC.L2-3.1.1 through AC.L2-3.1.22.',
        controls:    ['AC.L2-3.1.1', 'AC.L2-3.1.2', 'AC.L2-3.1.3', 'AC.L2-3.1.5', 'AC.L2-3.1.7'],
        pages:       '8-10 pages',
        generate:    generateAccessControlPolicy,
      },
    ],
  },
  {
    domain:    'AT',
    name:      'Awareness and Training',
    templates: [
      {
        id:          'at-policy',
        title:       'Security Awareness and Training Policy',
        description: 'Covers training requirements, insider threat awareness, and training records. Maps to AT.L2-3.2.1 through AT.L2-3.2.3.',
        controls:    ['AT.L2-3.2.1', 'AT.L2-3.2.2', 'AT.L2-3.2.3'],
        pages:       '5-6 pages',
        generate:    generateTrainingPolicy,
      },
    ],
  },
  {
    domain:    'CM',
    name:      'Configuration Management',
    templates: [
      {
        id:          'cm-policy',
        title:       'Configuration Management Policy',
        description: 'Covers baseline configurations, change control, software management, and vulnerability management. Maps to CM.L2-3.4.1 through CM.L2-3.4.9.',
        controls:    ['CM.L2-3.4.1', 'CM.L2-3.4.2', 'CM.L2-3.4.3', 'CM.L2-3.4.6'],
        pages:       '6-8 pages',
        generate:    generateConfigManagementPolicy,
      },
    ],
  },
  {
    domain:    'IR',
    name:      'Incident Response',
    templates: [
      {
        id:          'ir-policy',
        title:       'Incident Response Policy',
        description: 'Covers incident classification, response procedures, DoD reporting requirements, and recovery. Maps to IR.L2-3.6.1 through IR.L2-3.6.3.',
        controls:    ['IR.L2-3.6.1', 'IR.L2-3.6.2', 'IR.L2-3.6.3'],
        pages:       '7-9 pages',
        generate:    generateIncidentResponsePolicy,
      },
    ],
  },
  {
    domain:    'MP',
    name:      'Media Protection',
    templates: [
      {
        id:          'mp-policy',
        title:       'Media Protection Policy',
        description: 'Covers media access controls, transport, sanitization, and removable media. Maps to MP.L2-3.8.1 through MP.L2-3.8.9.',
        controls:    ['MP.L2-3.8.1', 'MP.L2-3.8.2', 'MP.L2-3.8.3', 'MP.L2-3.8.9'],
        pages:       '5-7 pages',
        generate:    generateMediaProtectionPolicy,
      },
    ],
  },
  {
    domain:    'RA',
    name:      'Risk Assessment',
    templates: [
      {
        id:          'ra-policy',
        title:       'Risk Assessment Policy',
        description: 'Covers risk assessment frequency, process, risk register, and vulnerability scanning. Maps to RA.L2-3.11.1 through RA.L2-3.11.3.',
        controls:    ['RA.L2-3.11.1', 'RA.L2-3.11.2', 'RA.L2-3.11.3'],
        pages:       '6-7 pages',
        generate:    generateRiskAssessmentPolicy,
      },
    ],
  },
  {
    domain:    'SC',
    name:      'System and Communications Protection',
    templates: [
      {
        id:          'sc-policy',
        title:       'System and Communications Protection Policy',
        description: 'Covers network segmentation, encryption, boundary protection, and remote access security. Maps to SC.L2-3.13.1 through SC.L2-3.13.16.',
        controls:    ['SC.L2-3.13.1', 'SC.L2-3.13.5', 'SC.L2-3.13.8', 'SC.L2-3.13.11'],
        pages:       '7-8 pages',
        generate:    generateSystemCommPolicy,
      },
    ],
  },
];


export default function PolicyLibrary({ profile, onUpgrade }) {
  const [downloading, setDownloading] = useState(null);
  const [search, setSearch]           = useState('');

  async function handleDownload(template) {
    setDownloading(template.id);
    try {
      await template.generate(profile);
    } catch (err) {
      console.error('Download error:', err);
    }
    setDownloading(null);
  }

  const filtered = TEMPLATES.filter(domain =>
    domain.name.toLowerCase().includes(search.toLowerCase()) ||
    domain.domain.toLowerCase().includes(search.toLowerCase()) ||
    domain.templates.some(t => t.title.toLowerCase().includes(search.toLowerCase()))
  );

  const totalTemplates = TEMPLATES.reduce((acc, d) => acc + d.templates.length, 0);

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 24,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
            Policy Template Library
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {totalTemplates} professional Word document templates mapped to CMMC controls
          </div>
        </div>
        <input
          placeholder="Search templates..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 6, padding: '7px 12px', fontSize: 12,
            color: 'var(--text)', fontFamily: 'var(--font)', outline: 'none',
            width: 220,
          }}
        />
      </div>

      {/* Info Banner */}
      <div style={{
        background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
        borderRadius: 8, padding: '12px 16px', marginBottom: 20,
        fontSize: 12, color: 'var(--accent)', lineHeight: 1.6,
      }}>
        Each template is pre-filled with your company name and CAGE code from your profile.
        Download, customize with your specific procedures, and upload to your Evidence Locker.
        Templates are based on NIST SP 800-171 requirements and CMMC Level 2 assessment guides.
      </div>

      {/* Stats Row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 12, marginBottom: 24,
      }}>
        {[
          { label: 'Available Templates', value: totalTemplates,  color: 'var(--accent)' },
          { label: 'Domains Covered',     value: TEMPLATES.length, color: 'var(--green)' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '14px 18px',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              fontSize: 28, fontWeight: 700, color: s.color,
            }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Template Cards */}
      {filtered.map(domain => (
        <div key={domain.domain} style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 20, marginBottom: 14,
        }}>
          {/* Domain Header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 14, paddingBottom: 12,
            borderBottom: '1px solid var(--border)',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
              background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: 'var(--accent)',
              fontFamily: 'var(--mono)',
            }}>
              {domain.domain}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                {domain.name}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>
                {domain.templates.length} template{domain.templates.length !== 1 ? 's' : ''} available
              </div>
            </div>
          </div>

          {/* Templates */}
          {domain.templates.map(template => (
            <div key={template.id} style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', gap: 20,
              padding: '12px 0',
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 12, fontWeight: 600,
                  color: 'var(--text)', marginBottom: 4,
                }}>
                  {template.title}
                </div>
                <div style={{
                  fontSize: 11, color: 'var(--text3)',
                  lineHeight: 1.5, marginBottom: 8,
                }}>
                  {template.description}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                  {template.controls.map(c => (
                    <span key={c} style={{
                      fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--accent)',
                      background: 'var(--blue-bg)', padding: '2px 6px', borderRadius: 4,
                      border: '1px solid rgba(45,125,210,0.2)',
                    }}>{c}</span>
                  ))}
                  {template.controls.length < 5 && (
                    <span style={{
                      fontSize: 9, color: 'var(--text3)',
                      padding: '2px 6px',
                    }}>+ more</span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                  Approx. {template.pages} · Word Document (.docx)
                </div>
              </div>
              <TrialGate feature="policy_download" profile={profile} onUpgrade={onUpgrade}>
                <button
                  onClick={() => handleDownload(template)}
                  disabled={downloading === template.id}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: '8px 16px',
                    borderRadius: 6, cursor: downloading === template.id ? 'not-allowed' : 'pointer',
                    border: '1px solid var(--accent)',
                    background: downloading === template.id ? 'var(--surface)' : 'var(--accent)',
                    color: downloading === template.id ? 'var(--text3)' : '#fff',
                    fontFamily: 'var(--font)', whiteSpace: 'nowrap',
                    opacity: downloading === template.id ? 0.7 : 1,
                    transition: 'all 0.15s', flexShrink: 0,
                  }}
                >
                  {downloading === template.id ? 'Generating...' : 'Download'}
                </button>
              </TrialGate>
            </div>
          ))}
        </div>
      ))}

    </div>
  );
}